import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Mail, Lock, Eye, EyeOff, User, Building2 } from 'lucide-react';
import apiService from '../../services/api';
import AuthLayout from './AuthLayout';
import GoogleSignInButton from './GoogleSignInButton';

const SignupPage = ({ onSignup, onSwitchToLogin, onNeedsCompany }) => {
  const { t } = useTranslation();
  const [formData, setFormData] = useState({
    firstName: '',
    lastName: '',
    email: '',
    password: '',
    confirmPassword: '',
    companyName: '',
    platform: 'universal',
  });
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState('');
  const [showPassword, setShowPassword] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsLoading(true);
    setError('');

    if (formData.password !== formData.confirmPassword) {
      setError('Passwords do not match');
      setIsLoading(false);
      return;
    }

    if (formData.password.length < 8) {
      setError('Password must be at least 8 characters long');
      setIsLoading(false);
      return;
    }

    try {
      const signupData = {
        firstName: formData.firstName,
        lastName: formData.lastName,
        email: formData.email,
        password: formData.password,
        companyName: formData.companyName,
        platform: formData.platform,
      };

      const response = await apiService.signup(signupData);

      if (response.data.success) {
        const { token, user, redirect_url } = response.data;

        localStorage.setItem('user', JSON.stringify(user));
        localStorage.setItem('isLoggedIn', 'true');

        onSignup(token || null, {
          user,
          planId: user.planId || null,
          isFreemium: user.isFreemium || true,
          redirectUrl: redirect_url,
        });
      } else {
        setError(response.data.message || t('signup_failed'));
      }
    } catch (error) {
      setError(error.response?.data?.message || t('signup_failed'));
    } finally {
      setIsLoading(false);
    }
  };

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  return (
    <AuthLayout
      title={t('create_your_account')}
      subtitle={t('start_managing_staff')}
      footer={
        <p className="auth-switch">
          Already have an account?{' '}
          <button type="button" onClick={onSwitchToLogin} className="auth-link">
            Sign in
          </button>
        </p>
      }
    >
      <form onSubmit={handleSubmit} className="auth-form">
        {error && (
          <div className="auth-error" role="alert">
            {error}
          </div>
        )}

        <GoogleSignInButton onLogin={onSignup} onError={setError} onNeedsCompany={onNeedsCompany} />

        <div className="auth-divider">
          <span>or sign up with email</span>
        </div>

        <div className="auth-row">
          <div className="auth-field">
            <label htmlFor="firstName" className="auth-label">
              First name
            </label>
            <div className="auth-input-wrap">
              <User size={18} className="auth-input-icon" />
              <input
                id="firstName"
                name="firstName"
                type="text"
                required
                className="auth-input"
                placeholder="Jane"
                value={formData.firstName}
                onChange={handleChange}
                autoComplete="given-name"
              />
            </div>
          </div>

          <div className="auth-field">
            <label htmlFor="lastName" className="auth-label">
              Last name
            </label>
            <div className="auth-input-wrap">
              <User size={18} className="auth-input-icon" />
              <input
                id="lastName"
                name="lastName"
                type="text"
                required
                className="auth-input"
                placeholder="Doe"
                value={formData.lastName}
                onChange={handleChange}
                autoComplete="family-name"
              />
            </div>
          </div>
        </div>

        <div className="auth-field">
          <label htmlFor="companyName" className="auth-label">
            Shop / company name
          </label>
          <div className="auth-input-wrap">
            <Building2 size={18} className="auth-input-icon" />
            <input
              id="companyName"
              name="companyName"
              type="text"
              required
              className="auth-input"
              placeholder="Acme Inc."
              value={formData.companyName}
              onChange={handleChange}
              autoComplete="organization"
            />
          </div>
        </div>

        <div className="auth-field">
          <label htmlFor="email" className="auth-label">
            Email address
          </label>
          <div className="auth-input-wrap">
            <Mail size={18} className="auth-input-icon" />
            <input
              id="email"
              name="email"
              type="email"
              required
              className="auth-input"
              placeholder="you@company.com"
              value={formData.email}
              onChange={handleChange}
              autoComplete="email"
            />
          </div>
        </div>

        <div className="auth-field">
          <label htmlFor="password" className="auth-label">
            Password
          </label>
          <div className="auth-input-wrap">
            <Lock size={18} className="auth-input-icon" />
            <input
              id="password"
              name="password"
              type={showPassword ? 'text' : 'password'}
              required
              className="auth-input"
              placeholder="At least 8 characters"
              value={formData.password}
              onChange={handleChange}
              autoComplete="new-password"
            />
            <button
              type="button"
              className="auth-input-toggle"
              onClick={() => setShowPassword((v) => !v)}
              aria-label={showPassword ? 'Hide password' : 'Show password'}
            >
              {showPassword ? <EyeOff size={18} /> : <Eye size={18} />}
            </button>
          </div>
        </div>

        <div className="auth-field">
          <label htmlFor="confirmPassword" className="auth-label">
            Confirm password
          </label>
          <div className="auth-input-wrap">
            <Lock size={18} className="auth-input-icon" />
            <input
              id="confirmPassword"
              name="confirmPassword"
              type={showPassword ? 'text' : 'password'}
              required
              className="auth-input"
              placeholder="Re-enter your password"
              value={formData.confirmPassword}
              onChange={handleChange}
              autoComplete="new-password"
            />
          </div>
        </div>

        <button type="submit" className="auth-submit" disabled={isLoading}>
          {isLoading ? 'Creating account…' : 'Create account'}
        </button>
      </form>
    </AuthLayout>
  );
};

export default SignupPage;
